// SEQ-240: "How is this worked out" text for the Dashboard figures built on
// useVariationsStats: contract value, claims to date, variations, the
// variations summary and trend, the two Top Projects charts and Project
// Health Overview. Edit this file whenever those calculations change.
//
// WHAT IT DESCRIBES (line numbers at commit 3eed3d4):
// - useVariationsStats.ts 89-93: active projects only (status active or blank).
// - useVariationsStats.ts 165-179: contract value per project, the sum of its
//   schedule lines, or a category's set amount when it is priced as one sum.
// - useVariationsStats.ts 181-187: claims to date, every claims_progress amount
//   whose schedule line belongs to the project.
// - variationStatsMath.ts: only a variation whose Dashpivot status is "valid"
//   (or blank) counts; invalid and pending-status ones are left out of every
//   figure below. Per counted variation: total after discount; credits
//   subtract; approved when approved_amount is above zero; waiting is total
//   minus approved, floored at zero below one cent.
// - useVariationsStats.ts, the main variation loop and the monthly summary
//   loop: both skip a variation removed from Dashpivot, then any status other
//   than valid, before adding anything.
// - useDashboardFilteredData.ts recalcVariationStats and the monthly trend
//   filter: with any filter set, the This Month, Last Month and Year to Date
//   cards and the period cards come from the monthly trend instead (the same
//   valid variations), which is narrowed by the date filter but never by
//   project.
// - Dashboard.tsx, projectsOverLimitCount and the first three cards, the
//   Monthly Variations Summary, Variations Trend, both Top Projects charts and
//   Project Health Overview; DashboardCharts.tsx VariationsTrendChart
//   (slice(-12)) and TopProjectsChart; ProjectHealthPanel.tsx.
//
// Every id below must be used by a component and every id a component uses
// must be here: dashboardFigureDefinitions.test.ts checks both directions.

import type { MetricDefinition } from "../components/howWorkedOut/metricDefinition";

const FROM_SCHEDULE_AND_CLAIMS =
  "From each project's schedule and the progress claim amounts entered against it.";
const FROM_VARIATIONS =
  "From the variations synced from Dashpivot. Variations flagged as removed from Dashpivot are left out.";
const VALID_ONLY = "Only variations marked valid in Dashpivot count. Invalid and pending-status ones are left out.";

const contractValueLine =
  "Original contract: each project's schedule lines added up (a category priced as one set amount counts that amount).";
const approvedLine = "Approved variations: the approved amount of every valid variation that has one. Credit variations subtract theirs.";

export const VARIATION_FIGURES = {
  "claims-progress": {
    name: "Overall claims progress",
    meaning: "How much of the original contract value has been claimed so far, across the projects in view.",
    formula: [
      "Total claimed divided by the original contract, times 100.",
      "Uses the original contract, not the revised total, so approved variations do not lower the percentage.",
      "Goes past 100% when more has been claimed than the original contract.",
    ],
    source: { kind: "calculated", detail: FROM_SCHEDULE_AND_CLAIMS },
    projects: { scope: "active-only" },
    dates: { scope: "all-time" },
    period: "All claims ever entered, up to today.",
  },
  "claims-progress.total-claimed": {
    name: "Total claimed",
    meaning: "Everything claimed so far on the projects in view.",
    formula: [
      "Adds up every progress claim amount entered against each project's schedule lines, for all time.",
      "Then adds the projects together.",
    ],
    source: { kind: "calculated", detail: FROM_SCHEDULE_AND_CLAIMS },
    projects: { scope: "active-only" },
    dates: { scope: "all-time" },
    period: "All claims ever entered, up to today.",
  },
  "claims-progress.total-revised": {
    name: "Total revised",
    meaning: "The contract value once approved variations are added.",
    formula: ["Original contract plus approved variations.", contractValueLine, approvedLine],
    source: { kind: "calculated", detail: `${FROM_SCHEDULE_AND_CLAIMS} ${FROM_VARIATIONS}` },
    projects: { scope: "active-only" },
    dates: { scope: "all-time" },
    period: "The contract as it stands today.",
  },
  "contract-bridge.original": {
    name: "Original contract",
    meaning: "The contract value before any variation.",
    formula: [contractValueLine, "Then adds the projects together."],
    source: { kind: "calculated", detail: "From each project's schedule." },
    projects: { scope: "active-only" },
    dates: { scope: "all-time" },
    period: "The schedule as it stands today.",
  },
  "contract-bridge.approved": {
    name: "Approved variations",
    meaning: "The value of variation work the builder has approved.",
    formula: [approvedLine, VALID_ONLY],
    source: { kind: "calculated", detail: FROM_VARIATIONS },
    projects: { scope: "active-only" },
    dates: { scope: "all-time" },
    period: "Every variation ever raised, up to today.",
  },
  "contract-bridge.revised": {
    name: "Revised total",
    meaning: "The contract value once approved variations are added.",
    formula: ["Original contract plus approved variations.", contractValueLine, approvedLine],
    source: { kind: "calculated", detail: `${FROM_SCHEDULE_AND_CLAIMS} ${FROM_VARIATIONS}` },
    projects: { scope: "active-only" },
    dates: { scope: "all-time" },
    period: "The contract as it stands today.",
  },
  "variations.critical": {
    name: "Critical projects",
    meaning: "How many projects have claimed more than their revised contract.",
    formula: [
      "Counts the projects where total claimed is more than the original contract plus approved variations.",
      "The badge shows only when at least one project is over.",
      VALID_ONLY,
    ],
    source: { kind: "calculated", detail: `${FROM_SCHEDULE_AND_CLAIMS} ${FROM_VARIATIONS}` },
    projects: { scope: "active-only" },
    dates: { scope: "all-time" },
    period: "All time, up to today.",
  },
  "variations.pending": {
    name: "Pending (blocked)",
    meaning: "Variation value still waiting for the builder to approve it.",
    formula: [
      "For each variation: its total after any discount, minus the amount approved so far. Less than one cent counts as nothing.",
      "Credit variations subtract their waiting amount.",
      "Awaiting approval on N items: the number of variations with an amount still waiting.",
      VALID_ONLY,
    ],
    source: { kind: "calculated", detail: FROM_VARIATIONS },
    projects: { scope: "active-only" },
    dates: { scope: "all-time" },
    period: "Every variation ever raised, as it stands today.",
  },
  "variations.net-approved": {
    name: "Net approved impact",
    meaning: "Approved additions minus approved credits.",
    formula: [approvedLine, VALID_ONLY],
    source: { kind: "calculated", detail: FROM_VARIATIONS },
    projects: { scope: "active-only" },
    dates: { scope: "all-time" },
    period: "Every variation ever raised, up to today.",
  },
  "variations.credits": {
    name: "Credits",
    meaning: "The value of every credit variation, approved or not.",
    formula: [
      "Adds up the total, after any discount, of every variation marked as a credit.",
      "Shown as a positive number. Approved and unapproved credits both count.",
      VALID_ONLY,
    ],
    source: { kind: "calculated", detail: FROM_VARIATIONS },
    projects: { scope: "active-only" },
    dates: { scope: "all-time" },
    period: "Every variation ever raised, up to today.",
  },
  "variations.additions": {
    name: "Additions",
    meaning: "The value of every variation that adds to the contract, approved or not.",
    formula: [
      "Adds up the total, after any discount, of every variation that is not a credit.",
      "Approved and unapproved variations both count.",
      VALID_ONLY,
    ],
    source: { kind: "calculated", detail: FROM_VARIATIONS },
    projects: { scope: "active-only" },
    dates: { scope: "all-time" },
    period: "Every variation ever raised, up to today.",
  },
  "variations-summary.this-month": {
    name: "Variations this month",
    meaning: "The value of variations raised for this calendar month.",
    formula: [
      "Adds up the total, after any discount, of variations whose claim month is this month. Credit variations subtract.",
      VALID_ONLY,
    ],
    source: { kind: "calculated", detail: FROM_VARIATIONS },
    projects: { scope: "active-ignores-filters" },
    dates: { scope: "calendar", detail: "This card shows only while the date filter is All time." },
    period: "This calendar month.",
  },
  "variations-summary.last-month": {
    name: "Variations last month",
    meaning: "The value of variations raised for last calendar month.",
    formula: [
      "Adds up the total, after any discount, of variations whose claim month is last month. Credit variations subtract.",
      VALID_ONLY,
    ],
    source: { kind: "calculated", detail: FROM_VARIATIONS },
    projects: { scope: "active-ignores-filters" },
    dates: { scope: "calendar", detail: "This card shows only while the date filter is All time." },
    period: "Last calendar month.",
  },
  "variations-summary.year-to-date": {
    name: "Variations year to date",
    meaning: "The value of variations raised so far this calendar year.",
    formula: [
      "Adds up the total, after any discount, of variations whose claim month is from January to this month of this year. Credit variations subtract.",
      VALID_ONLY,
    ],
    source: { kind: "calculated", detail: FROM_VARIATIONS },
    projects: { scope: "active-ignores-filters" },
    dates: { scope: "calendar", detail: "This card shows only while the date filter is All time." },
    period: "January to this month of this calendar year (not the financial year).",
  },
  "variations-summary.period-total": {
    name: "Variations total for the period",
    meaning: "The value of variations raised in the months of the date filter.",
    formula: [
      "Adds up the total, after any discount, of variations whose claim month falls in the date filter. Credit variations subtract.",
      VALID_ONLY,
    ],
    source: { kind: "calculated", detail: FROM_VARIATIONS },
    projects: { scope: "active-ignores-filters" },
    dates: { scope: "date-filter" },
    period: "The months in the date filter.",
  },
  "variations-summary.period-month": {
    name: "Variations for one month",
    meaning: "The value of variations raised in this month of the date filter.",
    formula: [
      "Adds up the total, after any discount, of variations whose claim month is this month. Credit variations subtract.",
      "Only the first two months in the range that have variations get a card.",
      VALID_ONLY,
    ],
    source: { kind: "calculated", detail: FROM_VARIATIONS },
    projects: { scope: "active-ignores-filters" },
    dates: { scope: "date-filter" },
    period: "The month named on the card.",
  },
  "variations-trend": {
    name: "Variations trend",
    meaning: "Variations raised and approved, month by month.",
    formula: [
      "Submitted: the total, after any discount, of variations raised for each claim month. Credit variations subtract.",
      "Approved: the approved amounts for those same variations.",
      "Shows the 12 most recent months that have any valid variation. Months with none are skipped, so the 12 points can span more than a year.",
      VALID_ONLY,
    ],
    source: { kind: "calculated", detail: FROM_VARIATIONS },
    projects: { scope: "active-ignores-filters" },
    dates: { scope: "date-filter" },
    period: "Up to 12 months that have variations.",
  },
  "top-projects.progress": {
    name: "Top projects by progress",
    meaning: "The five projects with the most claimed so far.",
    formula: [
      "Ranks projects by total claimed, all time, and shows the top five.",
      "Total claimed adds up every progress claim amount entered against the project's schedule lines.",
      "Click a bar to open that project.",
    ],
    source: { kind: "calculated", detail: FROM_SCHEDULE_AND_CLAIMS },
    projects: { scope: "active-only" },
    dates: { scope: "all-time" },
    period: "All claims ever entered, up to today.",
  },
  "top-projects.pending": {
    name: "Top projects by pending variations",
    meaning: "The five projects with the most variation value still waiting for approval.",
    formula: [
      "Ranks projects by their pending amount and shows the top five with more than zero.",
      "Pending for each variation: its total after any discount, minus the amount approved so far.",
      VALID_ONLY,
    ],
    source: { kind: "calculated", detail: FROM_VARIATIONS },
    projects: { scope: "active-only" },
    dates: { scope: "all-time" },
    period: "Every variation ever raised, as it stands today.",
  },
  "project-health": {
    name: "Project health overview",
    meaning: "One bar per project: how much is claimed against its contract, and whether it has gone over.",
    formula: [
      "Progress: total claimed divided by the original contract, times 100. The bar stops at 100%.",
      "Over limit: total claimed is more than the original contract plus approved variations.",
      VALID_ONLY,
      "Claimed, contract and variation figures come from active projects only, so a completed project listed here shows nothing claimed.",
    ],
    source: { kind: "calculated", detail: `${FROM_SCHEDULE_AND_CLAIMS} ${FROM_VARIATIONS}` },
    projects: { scope: "filters" },
    dates: { scope: "all-time" },
    period: "All time, up to today.",
  },
} satisfies Record<string, MetricDefinition>;
